import React, { FunctionComponent } from "react";
import SocialCard from "./SocialCard";
import SocialCardStack from "./SocialCardStack";
import { groupConsecutivePosts, PostGroup, ResourceList, SocialItem } from "../../service/data";
import { useSocialCardListStyles } from "./useSocialCardListStyles";
import {Box, Card, makeStyles} from "@material-ui/core";
import {Route} from "react-router";
import ShowNewContentButton from "../button/ShowNewContentButton";
import classNames from "classnames";
import Masonry from "react-masonry-component";
import {useContentStyles} from "../layout/useContentStyles";

interface SocialCardListProps {
    data: ResourceList<SocialItem>;
    page: number;
    pageSize: number;
    showCounter?: boolean;
    /**
     * Whether consecutive posts by one account fold into a stack. False on a single contributor's
     * page, where every post is by that contributor and stacking would fold the whole feed into one
     * card - the flooding it exists to prevent cannot happen when there is only one account.
     */
    stackConsecutive?: boolean;
    title?: string;
    total: number;
}

const SocialCardList: FunctionComponent<SocialCardListProps> = (props: SocialCardListProps) => {
    const classes = useSocialCardListStyles();
    const { data, page, pageSize, showCounter, stackConsecutive, total } = props;
    const masonryOptions = {
        transitionDuration: 0,
        itemSelector: '.masonryItem',
        gutter: 16,
        percentPosition: true,
    };

    /**
     * Consecutive posts by one account are folded into one expandable stack, so a burst cannot fill
     * the screen. Grouping the whole accumulated array rather than each page: SocialItemsList keeps
     * every page in one array, so a burst split across a page boundary regroups when the next page
     * arrives, and the API is not involved.
     *
     * A run of one comes back as a group of one, so both routes render every group the same way.
     */
    const renderGroup = (group: PostGroup) => (
        group.items.length > 1
            ? <SocialCardStack key={group.key} items={group.items} />
            : <SocialCard key={group.key} record={group.items[0]} />
    );

    // Not stacking is a minimum run length nothing can reach, so grouping keeps producing the same
    // one-group-per-item shape and the same keys it always does - no second code path to diverge.
    const groups = groupConsecutivePosts(data, stackConsecutive ? 2 : Number.POSITIVE_INFINITY);

    return (
        <>
            <Route exact path={["/", "/contributors/:id","/tw/:id", "/embed/tw/:id"]}>
                <Box className={classNames(classes.list)}>
                    <ShowNewContentButton />
                    {groups.map(renderGroup)}
                </Box>
            </Route>

            <Route exact path="/timeline">
                <Box className={classNames(classes.list, 'masonryWrapper')}>
                    <ShowNewContentButton />
                    <Masonry
                        enableResizableChildren={true}
                        className={classNames(classes.masonryLayout, 'masonryContainer')} // default ''
                        elementType={'div'} // default 'div'
                        options={masonryOptions} // default {}
                        disableImagesLoaded={false} // default false
                        updateOnEachImageLoad={false} // default false and works only if disableImagesLoaded is false
                    >
                    {groups.map(renderGroup)}
                    </Masonry>
                </Box>
            </Route>

        </>
    );
};

SocialCardList.defaultProps = {
    showCounter: false,
    stackConsecutive: true,
};

export default SocialCardList;
